import React, { useEffect, useRef, useState } from 'react';
import {
    BaseOperateView,
    BaseStagePlugin,
    getFramework,
    IFramework,
    useIdentity,
    useMoveEventListener,
} from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { ITouchKitEvent } from '@/utils/event-kit';
import { translatePointAngle } from '@/utils/math-kit';
import { traverseTrees } from '@/utils/tree-kit';
import {
    BaseLayerViewType,
    GroupRoleType,
    IExportPlugin,
    IGroupLayerProps,
    IImageLayerProps,
    ILayerProps,
    IPoint,
    LayerType,
    PluginType,
} from '@/version-types';
import './drop-img-plugin.less';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import type { ImageLayer } from '@/layers/image-layer';
import { applyResolvedImageReplacement, resolveImageReplacement } from '@/layers/image-layer/utils/layer';
import { DropImageHistory, HistoryType } from './drop-img-history';
import { checkIsImageDragSource, getLayerViewportCenter } from './utils';

const pointInLayer = (point: IPoint, centerPoint: IPoint, width: number, height: number) => {
    // 计算长方形的边界
    const left = centerPoint.x - width / 2;
    const right = centerPoint.x + width / 2;
    const top = centerPoint.y + height / 2;
    const bottom = centerPoint.y - height / 2;

    // 判断点是否在长方形内
    return point.x >= left && point.x <= right && point.y <= top && point.y >= bottom;
};

const checkCursorPointInRect = (layer: ILayerProps, point: IPoint, framework: IFramework) => {
    const centerPoint = getLayerViewportCenter(
        layer,
        framework.service.edgelessCanvas,
        framework.service.transform.calcLayerOffsetTop(layer),
    );
    const rotatePoint = {
        x: point.x - centerPoint.x,
        y: (point.y - centerPoint.y) * -1,
    };

    return pointInLayer(rotatePoint, { x: 0, y: 0 }, 84, 40);
};

/**
 * 检查鼠标是否在图片图层上
 *
 * @param layer - 图片图层
 * @param point - 鼠标位置
 * @returns 是否在图片图层上
 */
const checkCursorPointInImageLayer = (layer: ILayerProps, point: IPoint, framework: IFramework) => {
    const { width, height, rotate } = layer;
    const { zoom } = framework.service.edgelessCanvas;
    const centerPoint = getLayerViewportCenter(
        layer,
        framework.service.edgelessCanvas,
        framework.service.transform.calcLayerOffsetTop(layer),
    );
    const layerWidth = width * zoom;
    const layerHeight = height * zoom;
    const rotatePoint = translatePointAngle(
        {
            x: point.x - centerPoint.x,
            y: (point.y - centerPoint.y) * -1,
        },
        -rotate,
    );

    return pointInLayer(rotatePoint, { x: 0, y: 0 }, layerWidth, layerHeight);
};

const DropImgPluginView = React.memo(() => {
    const { $t } = useLocaleStore();
    const identity = useIdentity();
    const framework = getFramework(identity);
    const [targetLayer, setTargetLayer] = useState<ILayerProps | null>(null);
    const [canDrop, setCanDrop] = useState(false);
    const moveLayerId = useRef<string | null>(null);

    useEffect(() => {
        const handler = framework.service.frame.onBeforeBaseOperate((layer, operateType) => {
            moveLayerId.current = operateType === BaseLayerViewType.MOVE ? layer.id : null;
        });

        return () => framework.service.events.off(handler);
    }, [framework]);

    const checkIsPuzzle = (layer: ILayerProps) => {
        const parentId = layer.parentId;
        if (!parentId) return false;
        const parentLayer = framework.service.flatLayer.getLayer(parentId);

        if (!parentLayer) return false;
        return (
            parentLayer.layerType === LayerType.GROUP_LAYER &&
            (parentLayer.data as IGroupLayerProps).extension?.role === GroupRoleType.PUZZLE
        );
    };

    useMoveEventListener((e: ITouchKitEvent) => {
        const activeLayer = framework.service.frame.getActiveLayer();
        const currentMoveLayerId = moveLayerId.current;
        if (e.isEnd) moveLayerId.current = null;

        if (!activeLayer || !checkIsImageDragSource(e.target, activeLayer, currentMoveLayerId)) return;

        const layers = framework.service.frame.getActiveFrameLayers();
        const allImageLayers = [] as ILayerProps[];
        traverseTrees(layers, (layer) => {
            if (
                layer.layerType === LayerType.IMAGE_LAYER &&
                layer.id !== activeLayer.id &&
                ((layer.data as IImageLayerProps).maskURL || checkIsPuzzle(layer))
            ) {
                allImageLayers.push(layer);
            }
        });

        if (allImageLayers.length === 0) {
            return;
        }

        const currentPoint = framework.service.transform.getViewportPoint(e.currentPoint);
        let canDrop = false;
        let currentTargetLayer: ILayerProps | null = null;
        for (let i = 0; i < allImageLayers.length; i++) {
            const layer = allImageLayers[i];
            if (checkCursorPointInImageLayer(layer, currentPoint, framework)) {
                setTargetLayer(layer);
                currentTargetLayer = layer;

                if (checkCursorPointInRect(layer, currentPoint, framework)) {
                    setCanDrop(true);
                    canDrop = true;
                } else {
                    setCanDrop(false);
                    canDrop = false;
                }
                break;
            } else {
                setTargetLayer(null);
            }
        }

        if (e.isEnd) {
            setTargetLayer(null);
            setCanDrop(false);

            const dropTargetLayer = currentTargetLayer || targetLayer;
            if (!canDrop || !dropTargetLayer || !activeLayer) {
                return;
            }
            const dropLayer = cloneDeep(activeLayer);
            const oldTargetLayer = cloneDeep(dropTargetLayer);
            if (!checkIsPuzzle(oldTargetLayer)) framework.service.frame.setActiveLayer(oldTargetLayer.id);

            const dropIn = async () => {
                const replacement = await resolveImageReplacement(
                    (dropLayer.data as IImageLayerProps).url,
                    framework.service.edgelessCanvas.getSize().width,
                    (dropLayer.data as IImageLayerProps).naturalWidth,
                    (dropLayer.data as IImageLayerProps).naturalHeight,
                );

                const imageLayer = framework.service.registration.getInstance<ImageLayer>('ImageLayer');
                let latestDropLayer: ILayerProps | undefined;
                let latestTargetLayer: ILayerProps | undefined;
                const committedLayer = await imageLayer.updateLayerFromLatest(
                    oldTargetLayer.id,
                    (latestLayer) => {
                        latestDropLayer = framework.service.flatLayer.getLayer(dropLayer.id, true);
                        if (!latestDropLayer) return;
                        latestTargetLayer = cloneDeep(latestLayer);
                        return applyResolvedImageReplacement(
                            latestLayer,
                            replacement,
                            latestDropLayer.prompt || '',
                            latestDropLayer.materialIds || {},
                        );
                    },
                    {
                        activateLayer: false,
                        canCommit: () => !!framework.service.flatLayer.getLayer(dropLayer.id),
                        pushHistory: false,
                    },
                );
                if (!committedLayer || !latestDropLayer || !latestTargetLayer) return;
                framework.service.frame.delLayer(dropLayer.id);
                framework.service.history.write({
                    type: HistoryType.DROP_IMAGE,
                    data: {
                        item: {
                            fromLayer: cloneDeep(latestDropLayer),
                            oldTargetLayer: latestTargetLayer,
                            newTargetLayer: committedLayer,
                            layerIndex: framework.service.frame.getLayerFrameId(dropTargetLayer.id),
                        },
                    },
                    originIndex: dropTargetLayer.__originIndex,
                    needSyncRenderIds: true,
                });
            };

            void dropIn().catch((error) => {
                console.error('Drop image failed', error);
            });
        }
    });

    if (!targetLayer) {
        return null;
    }

    return (
        <BaseStagePlugin name="drop-img">
            <div className="l-render-drop-image">
                <BaseOperateView layer={targetLayer}>
                    <div
                        className={classNames({
                            'l-render-drop-image__btn': true,
                            'drag-in': !canDrop,
                            release: canDrop,
                        })}
                        style={{
                            transform: ` translate(-50%, -50%)) rotate(-${targetLayer.rotate}deg)`,
                        }}
                    >
                        {canDrop
                            ? $t((v) => v.imageLayer.renderDropImage.freedImage)
                            : $t((v) => v.imageLayer.renderDropImage.dropImage)}
                    </div>
                </BaseOperateView>
            </div>
        </BaseStagePlugin>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 3,
    name: 'drop-img',
    render() {
        return <DropImgPluginView />;
    },
    createHistory({ identity }) {
        return [new DropImageHistory(identity)];
    },
};

export default exportPlugin;
